import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import {
  Button,
  IconButton,
  MenuItem,
  Skeleton,
  Theme,
  Tooltip,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Formik } from 'formik';
import { useMemo } from 'react';
import * as Yup from 'yup';
import { useSnackbarWithStyles } from '../../app/components/notifications/SnackbarProviderWithStyles';
import { LabelledOutline } from '../../common/final-form-material-ui';
import { endingDate } from '../../recoil/atoms/endingDate';
import { startingDate } from '../../recoil/atoms/startingDate';

import { RouteComponentProps } from 'react-router-dom';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';
import { FormikSelect } from '../../common/formik-material-ui';
import { IUserAtom, user } from '../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
} from '../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../services/rest/getApiMutation';

import { useMutation } from 'react-query';
import { Business } from '../../lib/kixie-entities/tables/Business';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';

const useStyles = makeStyles((theme: Theme) => ({
  saveButton: {
    minWidth: '96px',
  },
  cancelButton: {
    backgroundColor: 'rgb(224, 224, 224)',
    minWidth: '96px',
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
    height: 'max-content',
    alignSelf: 'center',
  },
  companyName: {
    fontWeight: 'bold',
  },
  phraseKeywordList: {
    listStyleType: 'none',
    padding: 0,
  },
  pageContainer: {},
  topSectionContainer: {
    margin: '8px 64px',
    border: '1px solid #0098C7',
    backgroundColor: theme.palette.mode === 'dark' ? '#005C78' : '#DAEFF8',
    color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#005C78',
    padding: '16px',
  },
  topSectionTitleContainer: {
    padding: '8px 0px',
  },
  topSectionTitle: {
    fontSize: '20px',
  },
  topSectionSubheader: {},
  topSectionSubheaderContainer: {},
  fieldInsideRow: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
    width: '170px',
  },
}));

/* eslint-disable no-unused-vars */
/* eslint-enable no-unused-vars */

export default function PowerlistDispositionRules({
  history,
  match,
}: RouteComponentProps) {
  if (!history || !match) return null;
  const classes = useStyles();
  // BEGIN DATA FETCHING CODE COPIED FROM ../components/modals/ManageAccountModal
  const [userAtomLoadable, _setUserAtom] = useRecoilStateLoadable(user);
  const userAtom = userAtomLoadable.contents as IUserAtom;
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  const start = useRecoilValueLoadable<any>(startingDate);
  const end = useRecoilValueLoadable<any>(endingDate);
  const mutation = useMutation(getApiMutation(userAtom, _setUserAtom));

  const [businessCached] = makeApiRequestCached<Business>(
    {
      endpoint: `${endpointEnum.GET_BUSINESSES}/${userAtom.businessid}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.[0] ?? cached.current
  );

  const addNewPowerlistInitialValues = {
    updatedDate: new Date().toISOString(),
    createdDate: new Date().toISOString(),
    powerlistDispositionRulesDeleted: [],
    powerlistDispositionRules: [
      {
        businessDispositionId: '',
        action: '',
        status: 'active',
        forwardToCadenceId: '',
        forwardToPowerlistId: '',
        smsTemplateId: '',
      } as any,
    ],
  };

  const cadenceReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_CADENCE_REPORT}?filter=businessId:${userAtom.businessid}`,
    })
  );

  const cadences = useMemo(
    () =>
      cadenceReportLoadable.state === 'hasValue'
        ? [
            ...(cadenceReportLoadable.contents?.response?.data ?? []),
          ]?.sort((a, b) =>
            a?.cadenceName
              ?.toLowerCase()
              ?.localeCompare(b?.cadenceName?.toLowerCase())
          ) ?? []
        : [],
    [cadenceReportLoadable]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        powerlistDispositionRules: Yup.array().of(
          Yup.object().shape(
            {
              status: Yup.string(),
              action: Yup.string().when('businessDispositionId', {
                is: (val) => !!val,
                then: Yup.string().required('Selection is required'),
                otherwise: Yup.string(),
              }),
              businessDispositionId: Yup.number().when('action', {
                is: (val) => !!val,
                then: Yup.number().required('Selection is required'),
                otherwise: Yup.number(),
              }),
              forwardToCadenceId: Yup.number().when('action', {
                is: (val) => val === 'forwardToCadence',
                then: Yup.number().required('Selection is required'),
                otherwise: Yup.number(),
              }),
              forwardToPowerlistId: Yup.number().when('action', {
                is: (val) => val === 'forwardToPowerlist',
                then: Yup.number().required('Selection is required'),
                otherwise: Yup.number(),
              }),
              smsTemplateId: Yup.number().when('action', {
                is: (val) => val === 'sendSMS',
                then: Yup.number().required('Selection is required'),
                otherwise: Yup.number(),
              }),
            },
            [['action', 'businessDispositionId']]
          )
        ),
      }),
    []
  );

  const [
    _,
    powerlistDispositionRules,
    setRefetchPowerlistDispositionRules,
  ] = makeApiRequestCached(
    {
      endpoint: 'legacy/getBusinessPowerlistDispositionRules',
      method: 'POST',
      payloadJsonString: JSON.stringify({
        call: 'getBusinessPowerlistDispositionRules',
        businessId: userAtom.businessid,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.data ?? cached.current
  );

  const defaultDispositionRules = useMemo(() => {
    try {
      return JSON.parse(
        powerlistDispositionRules?.contents?.response?.data?.result?.[0]
          ?.defaultDispositionRules
      );
    } catch (e) {
      return [];
    }
  }, [powerlistDispositionRules]);

  const initialValues = useMemo(() => {
    return defaultDispositionRules?.length
      ? {
          updatedDate: new Date().toISOString(),
          createdDate: new Date().toISOString(),
          powerlistDispositionRulesDeleted: [],
          powerlistDispositionRules: defaultDispositionRules,
        }
      : addNewPowerlistInitialValues;
  }, [defaultDispositionRules]);

  const powerlistReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_BUSINESS_POWERLISTS}`,
      method: 'POST',
      payloadJsonString: JSON.stringify({
        call: 'v1/getBusinessPowerlists',
      }),
    }) as any
  );

  const powerlists = useMemo(
    () =>
      //else parse business powerlists data payload
      powerlistReportLoadable?.contents?.response?.data?.powerlists ??
      powerlistReportLoadable?.contents?.respowerlists ??
      [],
    [powerlistReportLoadable]
  );

  const data = {
    call: methodNameEnum.GET_SMS_TEMPLATE,
  };
  const smsTemplates = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_SMS_TEMPLATE,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const smsTemplateOptions = useMemo(
    () =>
      smsTemplates.state === 'hasValue'
        ? [...(smsTemplates.contents?.response?.data?.result ?? [])]
            ?.filter((template) => template.hasCustomField === 0)
            .sort((a, b) =>
              a.smsTemplateName
                ?.toLocaleLowerCase()
                ?.localeCompare(b?.smsTemplateName?.toLocaleLowerCase())
            )
        : [],
    [smsTemplates]
  );

  const disposition = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_USER_DISPOSITION_REPORTING,
      payloadJsonString: JSON.stringify({
        startdate: start.contents,
        enddate: end.contents,
        timezone: businessCached.current?.timezone?.timezonePhp,
      }),
      waitForPayload: !businessCached.current?.timezone?.timezonePhp,
    })
  );

  const dispoList = useMemo(
    () =>
      disposition.state === 'hasValue'
        ? disposition.contents?.data?.dispositions?.result
        : [],
    [disposition]
  );

  const filterPowerlists = useMemo(() => {
    let unsortedPowerLists: {
      name: string;
    }[] = [];
    unsortedPowerLists = Array.isArray(powerlists)
      ? unsortedPowerLists.concat(powerlists)
      : unsortedPowerLists;
    return Array.isArray(unsortedPowerLists)
      ? unsortedPowerLists.sort((a, b) =>
          a?.name?.toLowerCase() > b?.name?.toLowerCase() ? 1 : -1
        )
      : [];
  }, [powerlists]);

  const isLoading = useMemo(() => {
    return (
      !(cadenceReportLoadable.state === 'hasValue') &&
      disposition.state === 'hasValue' &&
      powerlistReportLoadable.state === 'hasValue' &&
      powerlistDispositionRules.state === 'hasValue' &&
      smsTemplates.state === 'hasValue'
    );
  }, [
    cadenceReportLoadable,
    disposition,
    powerlistReportLoadable,
    powerlistDispositionRules,
    smsTemplates,
  ]);

  return (
    <div className={classes.pageContainer}>
      <Formik
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          // console.log(response, 'response of blocking number');
          const dispoMap = {};
          for (const dispo of dispoList) {
            dispoMap[dispo.businessDispositionid] = dispo.disposition;
          }
          try {
            const response = await mutation.mutateAsync({
              endpoint: 'legacy/upsertBusinessPowerlistDispositionRules',
              payload: {
                userid: userAtom?.userid,
                businessId: userAtom?.businessid,
                properties: values.powerlistDispositionRules
                  .filter((dispoRule) => dispoRule?.businessDispositionId)
                  .map((dispoRule) => ({
                    ...dispoRule,
                    disposition: dispoMap[dispoRule.businessDispositionId],
                  })),
                call: 'upsertBusinessPowerlistDispositionRules',
              },
            });

            if (!response?.data?.success) {
              enqueueSnackbarWithStyles({
                message:
                  response?.data?.error ??
                  'An error occurred updating business powerlist call outcome rules. Please try again later.',
                alertProps: {
                  severity: 'error',
                },
              });
            } else {
              enqueueSnackbarWithStyles({
                message: 'Business powerlist call outcome rules updated',
                alertProps: {
                  severity: 'success',
                },
              });
            }
          } catch (e) {
            // console.log('replaceNumber error', e);
          }
          actions.resetForm({});
          setRefetchPowerlistDispositionRules({});
        }}
        //   validationSchema={}
        initialValues={initialValues}
      >
        {(formik) => {
          const filterCadences = (index: number) => {
            const correspondingCDid =
              formik.values.powerlistDispositionRules[index]
                ?.forwardToCadenceId;
            const dispoId =
              formik.values.powerlistDispositionRules[index]
                ?.businessDispositionId;
            const arrOfSelectedCadenceIds: any[] = [];
            for (
              let i = 0;
              i < formik.values.powerlistDispositionRules?.length;
              i++
            ) {
              const r = formik.values.powerlistDispositionRules[i];
              if (
                r.action !== 'remove' &&
                r.businessDispositionId == dispoId &&
                r.forwardToCadenceId
              ) {
                arrOfSelectedCadenceIds.push(r.forwardToCadenceId);
              }
            }
            return cadences
              ? cadences
                  ?.filter(
                    (cadence) =>
                      cadence.cadenceId === correspondingCDid ||
                      !arrOfSelectedCadenceIds.includes(cadence.cadenceId)
                  )
                  ?.sort((a, b) =>
                    a?.name
                      ?.toLowerCase()
                      ?.localeCompare(b?.name?.toLowerCase())
                  )
              : [];
          };

          const filterOptions = (index: number) => {
            const currDispoId =
              formik.values.powerlistDispositionRules?.[index]
                ?.businessDispositionId;
            let firstIndex: number | undefined;
            for (
              let i = 0;
              i < formik.values.powerlistDispositionRules.length;
              i++
            ) {
              const rule = formik.values.powerlistDispositionRules[i];

              if (
                Number(rule.businessDispositionId) === Number(currDispoId) &&
                rule.action === 'remove'
              ) {
                if (!firstIndex) {
                  firstIndex = i;
                }
              }
            }
            const firstStop = index === firstIndex;

            if (firstIndex === undefined || firstStop) {
              return [
                { value: 'sendSMS', text: 'Send SMS Template' },
                { value: 'remove', text: 'Remove From PowerList' },
                { value: 'forwardToCadence', text: 'Forward To Cadence' },
                { value: 'forwardToPowerlist', text: 'Forward To PowerList' },
                { value: 'assignOwner', text: 'Assign Owner' },
              ];
            } else {
              return [
                { value: 'sendSMS', text: 'Send SMS Template' },
                { value: 'forwardToCadence', text: 'Forward To Cadence' },
                { value: 'forwardToPowerlist', text: 'Forward To PowerList' },
                { value: 'assignOwner', text: 'Assign Owner' },
              ];
            }
          };

          const filterSMSTemplates = (index: number) => {
            const correspondingTempladeId =
              formik.values.powerlistDispositionRules[index]?.smsTemplateId;
            const dispoId =
              formik.values.powerlistDispositionRules[index]
                ?.businessDispositionId;
            const arrOfSelectedTemplateIds: any[] = [];
            for (
              let i = 0;
              i < formik.values.powerlistDispositionRules?.length;
              i++
            ) {
              const r = formik.values.powerlistDispositionRules[i];
              if (
                r.action !== 'remove' &&
                r.smsTemplateId &&
                r.businessDispositionId == dispoId
              ) {
                arrOfSelectedTemplateIds.push(r.smsTemplateId);
              }
            }

            return smsTemplateOptions
              ? smsTemplateOptions
                  ?.filter(
                    (template) =>
                      template.smsTemplateid === correspondingTempladeId ||
                      !arrOfSelectedTemplateIds.includes(template.smsTemplateid)
                  )
                  ?.sort((a, b) =>
                    a?.name
                      ?.toLowerCase()
                      ?.localeCompare(b?.name?.toLowerCase())
                  )
              : [];
          };

          const dispoIdList = useMemo(() => {
            const dispoMap = {};
            formik.values.powerlistDispositionRules.forEach(
              (powerlistDispositionRule) => {
                dispoMap[powerlistDispositionRule.businessDispositionId] = true;
              }
            );
            return dispoMap;
          }, [formik.values.powerlistDispositionRules, dispoList]);

          const filteredDispoList = useMemo(() => {
            return (
              [...(dispoList ?? [])].sort((a, b) =>
                a?.disposition
                  ?.toString()
                  ?.toLowerCase()
                  ?.localeCompare(b?.disposition?.toString().toLowerCase())
              ) ?? []
            );
          }, [dispoIdList]);

          return (
            <div>
              <div className={classes.topSectionContainer}>
                <div className={classes.topSectionTitleContainer}>
                  <p
                    className={classes.topSectionTitle}
                  >{`PowerList Call Outcome Rules`}</p>
                </div>
                <div className={classes.topSectionSubheaderContainer}>
                  <p
                    className={classes.topSectionSubheader}
                  >{`When a new organizational or personal PowerList is created, the below Call Outcome Rules will be automatically applied to that PowerList. If the rules are changed within that PowerList configuration, Kixie will respect the custom rules assigned to that PowerList. Making changes to the below rules will automatically be applied to any PowerList that uses these Account Default Call Outcome Rules.`}</p>
                </div>
              </div>
              {isLoading ? (
                <div>
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 5, marginTop: 20 }}
                  />
                  <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 15, width: '50%' }}
                  />
                  <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 15, width: '50%' }}
                  />
                  <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 15, width: '50%' }}
                  />
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 5, marginTop: 20 }}
                  />
                  <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 15, width: '50%' }}
                  />
                  <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 15, width: '50%' }}
                  />
                  <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 15, width: '50%' }}
                  />
                </div>
              ) : (
                formik.values.powerlistDispositionRules.map((dispo, index) => (
                  <div
                    key={index}
                    className="row"
                    style={{
                      flexWrap: 'inherit',
                      margin: '36px 64px',
                      width: '',
                    }}
                  >
                    <div className="col-11">
                      <LabelledOutline
                        key={index}
                        label={`Call Outcome Rule ${index + 1}`}
                      >
                        <FormikSelect
                          name={`powerlistDispositionRules.${index}.businessDispositionId`}
                          className={classes.fieldInsideRow}
                          formik={formik}
                          label="Call Outcome"
                          margin="dense"
                          variant="outlined"
                        >
                          {!!formik.values.powerlistDispositionRules[index]
                            .businessDispositionId && (
                            <MenuItem
                              value={
                                Number(
                                  formik.values.powerlistDispositionRules[index]
                                    ?.businessDispositionId
                                ) ?? ''
                              }
                            >
                              {(dispoList ?? []).find(
                                (d) =>
                                  Number(
                                    formik.values.powerlistDispositionRules[
                                      index
                                    ].businessDispositionId
                                  ) === Number(d.businessDispositionid)
                              )?.disposition ?? ''}
                            </MenuItem>
                          )}
                          {filteredDispoList?.map((dispo, index) => (
                            <MenuItem
                              key={index}
                              value={dispo.businessDispositionid}
                            >
                              {dispo.disposition}
                            </MenuItem>
                          )) ?? []}
                        </FormikSelect>
                        <FormikSelect
                          name={`powerlistDispositionRules.${index}.action`}
                          className={classes.fieldInsideRow}
                          formik={formik}
                          label="Action"
                          margin="dense"
                          variant="outlined"
                        >
                          {filterOptions(index).map((option, index) => (
                            <MenuItem value={option.value} key={index}>
                              {option.text}
                            </MenuItem>
                          ))}
                        </FormikSelect>
                        {formik.values.powerlistDispositionRules[index]
                          .action === 'forwardToCadence' && (
                          <FormikSelect
                            name={`powerlistDispositionRules.${index}.forwardToCadenceId`}
                            className={classes.fieldInsideRow}
                            formik={formik}
                            label="Cadence"
                            margin="dense"
                            variant="outlined"
                          >
                            {filterCadences(index).map((cadence, index) => (
                              <MenuItem key={index} value={cadence.cadenceId}>
                                {cadence.cadenceName}
                              </MenuItem>
                            ))}
                          </FormikSelect>
                        )}
                        {formik.values.powerlistDispositionRules[index]
                          .action === 'forwardToPowerlist' && (
                          <FormikSelect
                            name={`powerlistDispositionRules.${index}.forwardToPowerlistId`}
                            className={classes.fieldInsideRow}
                            formik={formik}
                            label="PowerList"
                            margin="dense"
                            variant="outlined"
                          >
                            {filterPowerlists?.map((powerlist, index) => (
                              <MenuItem
                                key={index}
                                value={powerlist.powerlistId}
                              >
                                {powerlist.name}
                              </MenuItem>
                            ))}
                          </FormikSelect>
                        )}
                        {formik.values.powerlistDispositionRules[index]
                          .action === 'sendSMS' && (
                          <FormikSelect
                            name={`powerlistDispositionRules.${index}.smsTemplateId`}
                            className={classes.fieldInsideRow}
                            formik={formik}
                            label="SMS Template Name"
                            margin="dense"
                            variant="outlined"
                          >
                            {filterSMSTemplates(index).map(
                              (smsTemplate, index) => (
                                <MenuItem
                                  key={index}
                                  value={smsTemplate.smsTemplateid}
                                >
                                  {smsTemplate.smsTemplateName}
                                </MenuItem>
                              )
                            )}
                          </FormikSelect>
                        )}
                      </LabelledOutline>
                    </div>
                    <div
                      className="col-1"
                      style={{
                        paddingLeft: '0px',
                        display: 'flex',
                        flexWrap: 'nowrap',
                      }}
                    >
                      <Tooltip title="add">
                        <span>
                          <IconButton
                            style={{ marginTop: '8px' }}
                            aria-label="add"
                            color="primary"
                            disabled={filteredDispoList?.length === 0}
                            onClick={() =>
                              formik.setFieldValue(
                                'powerlistDispositionRules',
                                [
                                  ...formik.values.powerlistDispositionRules.slice(
                                    0
                                  ),
                                  {
                                    businessDispositionId: '',
                                    action: '',
                                    forwardToCadenceId: '',
                                    status: 'active',
                                  },
                                ]
                              )
                            }
                          >
                            <PlaylistAddIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip key={index} title="remove">
                        <span>
                          <IconButton
                            className={classes.deleteIconButton}
                            style={{ marginTop: '8px' }}
                            aria-label="cancel"
                            onClick={() =>
                              formik.setFieldValue(
                                'powerlistDispositionRules',
                                formik.values.powerlistDispositionRules.length >
                                  1
                                  ? [
                                      ...formik.values.powerlistDispositionRules.slice(
                                        0,
                                        index
                                      ),
                                      ...formik.values.powerlistDispositionRules.slice(
                                        index + 1
                                      ),
                                    ]
                                  : [
                                      {
                                        businessDispositionId: '',
                                        action: '',
                                        forwardToCadenceId: '',
                                        status: '',
                                      },
                                    ]
                              )
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                ))
              )}
              <div>
                <div
                  style={{
                    display: 'flex',
                    marginBottom: '64px',
                    padding: '36px 36px 0px',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    className={classes.cancelButton}
                    onClick={() => formik.resetForm()}
                    disabled={!formik.dirty}
                  >
                    Cancel
                  </Button>
                  <div style={{ marginRight: '36px' }} />
                  <Button
                    onClick={formik.handleSubmit as any}
                    className={classes.saveButton}
                    variant="contained"
                    color="primary"
                    disabled={!formik?.isValid || !formik.dirty}
                  >
                    Save And Apply
                  </Button>
                </div>
              </div>
            </div>
          );
        }}
      </Formik>
    </div>
  );
}
